import type { ArtifactRef } from '../state/chatTypes';

const ARTIFACT_KINDS = ['table', 'chart'] as const;

const isArtifactKind = (value: unknown): value is ArtifactRef['kind'] =>
  typeof value === 'string' &&
  (ARTIFACT_KINDS as ReadonlyArray<string>).includes(value);

/**
 * Reads `Message.artifacts` from a wire message. Entries without an id or
 * with an unknown kind are dropped, and an empty list reads as `undefined`.
 * @param raw - The wire `artifacts` value.
 * @returns The artifacts, or `undefined` when there are none.
 */
export const parseWireArtifacts = (
  raw: unknown
): ReadonlyArray<ArtifactRef> | undefined => {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const artifacts = raw.flatMap((entry): ArtifactRef[] => {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) {
      return [];
    }
    const { id, kind, title, rowCount } = entry as Record<string, unknown>;
    if (typeof id !== 'string' || id === '' || !isArtifactKind(kind)) {
      return [];
    }
    return [
      {
        id,
        kind,
        title: typeof title === 'string' ? title : '',
        rowCount: typeof rowCount === 'number' ? rowCount : 0,
      },
    ];
  });
  return artifacts.length > 0 ? artifacts : undefined;
};
